import {
    Box,
    Card,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    TextField,
    CircularProgress,
    Button,
    ButtonGroup,
    useTheme,
    Tooltip as MuiTooltip,
    Paper
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { loadTimeseries, listSeries } from '../../../data/timeseriesClient';
import { SeriesConfig } from '../../../data/seriesConfig';
import { matchSeriesByTicker } from '../../../data/seriesUtils';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subDays, subYears, format } from 'date-fns';
import { de } from 'date-fns/locale';

import NumbersIcon from '@mui/icons-material/Numbers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ReplayIcon from '@mui/icons-material/Replay';
import InsightsIcon from '@mui/icons-material/Insights';
import {
    getClusterIds,
    computeClusterReturns,
    computeCumulativeReturns,
    calculateClusterMetrics,
    ClusterMetricMap,
    ClusterReturnSeries,
    MetricRuleSet,
} from './clustering/calculation'; // your helper file

import ClusteringPerformanceChart from './clustering/ClusteringPerformanceChart';
import ClusteringFeatureChart from './clustering/ClusteringFeatureChart';
import ClusteringMetricsChart from './clustering/ClusteringMetricsChart';
import ClusteringTimeChart from './clustering/ClusteringTimeChart';
import { ClusteringRadarChart } from './clustering/ClusteringRadarChart';
import { ClusterMetricsDisplay } from './clustering/ClusteringMetricsDisplay';
import { MetricRules } from './clustering/components/MetricRules';

export function useClusterMetrics(
    cumulativeReturns: Record<number, number>[],
    clusterIds: number[],
    timeseriesData: ClusterReturnSeries['timeseriesData'], // Add this parameter
): Record<number, ClusterMetricMap> {
    return useMemo(() => {
        if (!cumulativeReturns.length || timeseriesData.length != cumulativeReturns.length) return {};

        return Object.fromEntries(
            clusterIds.map((id) => {
                const returns: number[] = [];

                for (let i = 1; i < cumulativeReturns.length; i++) {
                    const prevData = timeseriesData[i - 1];

                    // Only include returns when this cluster was active in the previous period
                    if (prevData.activeClusterId === id) {
                        const prev = cumulativeReturns[i - 1][id];
                        const curr = cumulativeReturns[i][id];
                        if (prev > 0 && curr > 0) {
                            returns.push((curr / prev) - 1);
                        }
                    }
                }

                return [id, calculateClusterMetrics(returns)];
            })
        );
    }, [cumulativeReturns, clusterIds]);
}

export function useCurrentClusterFeatures(
    clusterReturnSeries: ClusterReturnSeries,
    clusterIds: number[]
): Record<number, Record<string, number>> {
    return useMemo(() => {
        if (!clusterReturnSeries.timeseriesData.length) return {};

        // Get the latest timeseries data point
        const latestData = clusterReturnSeries.timeseriesData[clusterReturnSeries.timeseriesData.length - 1];
        return latestData.clusterFeatures || {};
    }, [clusterReturnSeries.timeseriesData]);
}

export function useCombinedMetrics(
    combinedReturns: number[],
    enabledClusters: Record<number, boolean>
): ClusterMetricMap | null {
    return useMemo(() => {
        // const hasEnabledClusters = Object.values(enabledClusters).some(enabled => enabled);

        if (combinedReturns.length <= 1) {
            return null;
        }

        const returns: number[] = [];
        for (let i = 1; i < combinedReturns.length; i++) {
            const prev = combinedReturns[i - 1];
            const curr = combinedReturns[i];
            if (prev > 0 && curr > 0) {
                returns.push((curr / prev) - 1);
            }
        }

        return calculateClusterMetrics(returns);
    }, [combinedReturns, enabledClusters]);
}

interface ClusteringTabProps {
    defaultTicker?: string;
}

const ClusteringTab = ({ defaultTicker }: ClusteringTabProps) => {
    const theme = useTheme();
    // ==================== STATE MANAGEMENT ====================
    const [loading, setLoading] = useState(false);
    const [availableSeries, setAvailableSeries] = useState<SeriesConfig[]>([]);
    const [seriesLoading, setSeriesLoading] = useState(true);
    const [seriesError, setSeriesError] = useState<string | null>(null);
    const [selectedClustering, setSelectedClustering] = useState<SeriesConfig | null>(null);

    useEffect(() => {
        listSeries('clustering')
            .then(series => {
                setAvailableSeries(series);
                if (series.length > 0 && !selectedClustering) {
                    const match = defaultTicker
                        ? matchSeriesByTicker(series, defaultTicker)
                        : null;
                    setSelectedClustering(match || series[0]);
                }
            })
            .catch(err => {
                console.error('Failed to load clustering series', err);
                setSeriesError('Failed to load available clusterings');
            })
            .finally(() => setSeriesLoading(false));
    }, []);

    const [timeseriesData, setTimeseriesData] = useState<any[]>([]);
    const [fromDate, setFromDate] = useState<Date>(subYears(new Date(), 1));
    const [toDate, setToDate] = useState<Date>(new Date());

    const [showAllMetrics, setShowAllMetrics] = useState(false);
    const [metricRules, setMetricRules] = useState<MetricRuleSet>({
        groups: [],
        groupLogic: 'AND'
    });
    const [clusterAssignmentStrategy, setClusterAssignmentStrategy] = useState<string>('sharpe');

    // Core computed data states
    const [allocations, setAllocations] = useState<Record<number, number>>({});
    const [enabledClusters, setEnabledClusters] = useState<Record<number, boolean>>({});
    const [cumulativeReturns, setCumulativeReturns] = useState<Record<number, number>[]>([]);
    const [combinedReturns, setCombinedReturns] = useState<number[]>([]);

    const dateRangeButtons = [
        { label: '30 days', days: 30 },
        { label: '60 days', days: 60 },
        { label: '90 days', days: 90 },
        { label: '180 days', days: 180 },
        { label: '1 year', days: 365 },
        { label: '2 years', days: 730 },
        { label: '5 years', days: 1825 },
        { label: 'All', days: 10000 },
    ];

    useEffect(() => {
        if (!selectedClustering) return;

        const controller = new AbortController();
        setLoading(true);

        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);

        loadTimeseries(selectedClustering.series, { from: fromDate, to: endOfDay, signal: controller.signal })
            .then(data => setTimeseriesData(data))
            .catch(err => { if (err.name !== 'AbortError') console.error("Error fetching clustering timeseries data", err); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });

        return () => controller.abort();
    }, [selectedClustering, fromDate, toDate]);

    // Compute cluster IDs from filtered data
    const clusterIds = useMemo(() =>
        getClusterIds(timeseriesData),
        [timeseriesData]
    );

    // Add this new useMemo after the timeseriesData useEffect:
    const availableAssignmentStrategies = useMemo(() => {
        if (timeseriesData.length === 0) return ['sharpe'];

        const lastDataPoint = timeseriesData[timeseriesData.length - 1];
        const clusterAssignment = lastDataPoint?.data?.cluster_assignment;

        if (clusterAssignment && typeof clusterAssignment === 'object') {
            return Object.keys(clusterAssignment);
        }

        return ['sharpe']; // fallback
    }, [timeseriesData]);

    useEffect(() => {
        if (availableAssignmentStrategies.length > 0 && !availableAssignmentStrategies.includes(clusterAssignmentStrategy)) {
            setClusterAssignmentStrategy(availableAssignmentStrategies[0]);
        }
    }, [availableAssignmentStrategies, clusterAssignmentStrategy]);

    // Compute cluster return series
    const clusterReturnSeries = useMemo(() =>
        computeClusterReturns(timeseriesData, clusterIds, clusterAssignmentStrategy),
        [timeseriesData, clusterIds, clusterAssignmentStrategy]
    );

    const [turnoverCost, setTurnoverCost] = useState<number>(0);

    // Update allocations and enabledClusters when clusterIds change
    useEffect(() => {
        if (clusterIds.length === 0) return;

        setAllocations(prev =>
            Object.fromEntries(clusterIds.map(c => [c, prev[c] ?? 1]))
        );

        setEnabledClusters(prev =>
            Object.fromEntries(clusterIds.map(c => [c, prev[c] ?? false]))
        );
    }, [clusterIds]);

    // Calculate cumulative returns when all dependencies are ready
    useEffect(() => {
        const { individual: individualReturns, combined: combinedReturnData } = computeCumulativeReturns(
            clusterReturnSeries.timeseriesData,
            clusterIds,
            allocations,
            enabledClusters,
            metricRules,
            turnoverCost
        );

        setCumulativeReturns(individualReturns);
        setCombinedReturns(combinedReturnData);
    }, [clusterReturnSeries, clusterIds, allocations, enabledClusters, metricRules, turnoverCost]);

    // Calculate metrics only when cumulative returns are properly computed
    const clusterMetrics = useClusterMetrics(cumulativeReturns, clusterIds, clusterReturnSeries.timeseriesData);
    const combinedMetrics = useCombinedMetrics(combinedReturns, enabledClusters);
    const currentClusterFeatures = useCurrentClusterFeatures(clusterReturnSeries, clusterIds);

    const hasEnabledCluster = useMemo(() => {
        return Object.values(enabledClusters).some(enabled => enabled);
    }, [enabledClusters]);

    const hasEnabledRule = useMemo(() => {
        return metricRules.groups.some(group =>
            group.rules.some(rule => rule.enabled)
        );
    }, [metricRules]);

    const handleDateRangeClick = (days: number) => {
        const end = new Date();
        if (days >= 10000) {
            setFromDate(subYears(end, 10));
        } else {
            setFromDate(subDays(end, days));
        }
        setToDate(end);
    };

    const handleSliderChange = (cluster: number, value: number) => {
        setAllocations((prev) => ({ ...prev, [cluster]: value }));
    };

    const handleInputChange = (cluster: number, raw: string) => {
        const parsed = parseFloat(raw);
        setAllocations((prev) => ({
            ...prev,
            [cluster]: isNaN(parsed) ? 0 : parsed,
        }));
    };

    const handleCheckboxChange = (cluster: number, checked: boolean) => {
        setEnabledClusters((prev) => ({ ...prev, [cluster]: checked }));
    };

    // Add this custom tooltip component inside your main component
    const ClusterTooltip = ({ clusterId }: { clusterId: number }) => {
        const metrics = clusterMetrics[clusterId];
        const features = currentClusterFeatures[clusterId];

        if (!metrics && !features) return null;

        return (
            <Paper sx={{ p: 2, maxWidth: 350, bgcolor: 'background.paper' }}>
                <ClusterMetricsDisplay
                    clusterId={clusterId}
                    metrics={metrics ?? {}}
                    features={features}
                    allClustersData={currentClusterFeatures}
                    asset={clusterReturnSeries.asset}
                    showAllMetrics={true} // Always show all metrics in tooltip
                    radarChartSize={150} // Smaller size for tooltip
                />
            </Paper>
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Box pt={2} px={2}>
                <Box mb={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="clustering-select-label">Clustering</InputLabel>
                                <Select
                                    labelId="clustering-select-label"
                                    value={selectedClustering?.id || ''}
                                    onChange={(e) => {
                                        const selected = availableSeries.find(c => c.id === e.target.value);
                                        if (selected) {
                                            setSelectedClustering(selected);
                                            setTimeseriesData([]);
                                        }
                                    }}
                                    label="Clustering"
                                    disabled={seriesLoading}
                                >
                                    {availableSeries.map((clustering) => (
                                        <MenuItem key={clustering.id} value={clustering.id}>
                                            {clustering.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={2}>
                            <DatePicker
                                label="From"
                                value={fromDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) setFromDate(newValue);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <DatePicker
                                label="To"
                                value={toDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) setToDate(newValue);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(0, 4).map((range) => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() => handleDateRangeClick(range.days)}
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(4, 8).map((range) => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() => handleDateRangeClick(range.days)}
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Conditional loading / content */}
                {seriesError ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
                        <Typography color="error">{seriesError}</Typography>
                    </Box>
                ) : loading || !timeseriesData || timeseriesData.length === 0 ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
                        <CircularProgress />
                    </Box>
                ) : (

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Performance
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2} mt={2}>

                            <FormControl sx={{ maxWidth: 200 }}>
                                <InputLabel>Cluster Assignment</InputLabel>
                                <Select
                                    value={clusterAssignmentStrategy}
                                    onChange={(e) => setClusterAssignmentStrategy(e.target.value)}
                                    label="Cluster Assignment"
                                    size="small"
                                >
                                    {availableAssignmentStrategies.map((strategy) => (
                                        <MenuItem key={strategy} value={strategy}>
                                            {strategy.charAt(0).toUpperCase() + strategy.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* NEW: Floating number input for turnover cost */}
                            <TextField
                                label="Turnover Cost"
                                type="number"
                                size="small"
                                value={Number.isFinite(turnoverCost) ? turnoverCost : 0}
                                onChange={(e) => setTurnoverCost(Number(e.target.value) || 0)}
                                inputProps={{ step: 0.0001, min: 0, max: 1 }}
                                sx={{ width: 200 }}
                            />

                            <Button
                                variant={showAllMetrics ? 'contained' : 'outlined'}
                                onClick={() => setShowAllMetrics(!showAllMetrics)}
                            >
                                Metrics
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<ReplayIcon />}
                                onClick={() => {
                                    setEnabledClusters(Object.fromEntries(clusterIds.map(id => [id, false])));
                                    setAllocations(Object.fromEntries(clusterIds.map(id => [id, 1])));
                                    setClusterAssignmentStrategy('sharpe');
                                }}
                            >
                                Reset
                            </Button>
                        </Box>

                        <Card variant="outlined" sx={{ p: 2, mt: 2, "&:last-child": { paddingBottom: 0 } }}>
                            <Grid container spacing={2}>
                                {clusterIds.map((clusterId) => (
                                    <Grid item xs={12} sm={6} md={2.4} key={clusterId}>
                                        <MuiTooltip
                                            title={<ClusterTooltip clusterId={clusterId} />}
                                            placement="top"
                                            arrow
                                            disableHoverListener={showAllMetrics}
                                            enterDelay={500}
                                            leaveDelay={200}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'transparent',
                                                        '& .MuiTooltip-arrow': {
                                                            color: theme.palette.background.paper,
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <Card
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 0.5,
                                                    padding: 1,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    border: `1px solid ${enabledClusters[clusterId] ? theme.palette.secondary.main : 'rgba(255, 255, 255, 0.12)'}`,
                                                    bgcolor: enabledClusters[clusterId] ? theme.palette.secondary.main + '0D' : 'transparent',
                                                    '&:hover': {
                                                        borderColor: enabledClusters[clusterId]
                                                            ? theme.palette.secondary.light
                                                            : 'rgba(255, 255, 255, 0.3)',
                                                        bgcolor: enabledClusters[clusterId]
                                                            ? theme.palette.secondary.main + '14'
                                                            : 'rgba(255, 255, 255, 0.02)'
                                                    }
                                                }}
                                                onClick={() => handleCheckboxChange(clusterId, !enabledClusters[clusterId])}
                                            >
                                                {/* your content here - no CardContent needed since you're overriding everything */}

                                                {/* First row: ID + controls */}
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Box display="flex" alignItems="center" sx={{ width: 160 }}>
                                                        <NumbersIcon sx={{ mr: 1, fontSize: 28, verticalAlign: 'middle', color: 'primary.main' }} />
                                                        <Typography variant="h6" sx={{ lineHeight: 1 }}>
                                                            {clusterId}
                                                        </Typography>
                                                    </Box>

                                                    <Slider
                                                        min={-1}
                                                        max={1}
                                                        step={0.01}
                                                        value={allocations[clusterId] ?? 1}
                                                        onChange={(_, value) => {
                                                            typeof value === 'number' && handleSliderChange(clusterId, value);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            flexGrow: 1, // Takes remaining space
                                                            mx: 0.5,
                                                            mr: 2,
                                                            '& .MuiSlider-thumb': {
                                                                width: 10,
                                                                height: 10,
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.1)',
                                                                },
                                                                '&.Mui-active': {
                                                                    boxShadow: '0 0 0 4px rgba(0, 0, 0, 0.15)',
                                                                },
                                                                '&.Mui-focusVisible': {
                                                                    boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
                                                                },
                                                            },
                                                            '& .MuiSlider-rail': {
                                                                opacity: 0.3,
                                                            },
                                                        }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={allocations[clusterId] ?? 1}
                                                        onChange={(e) => handleInputChange(clusterId, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ width: 140 }}
                                                        inputProps={{
                                                            step: 0.01,
                                                            inputMode: 'decimal',
                                                            style: {
                                                                padding: '4px 4px 4px 8px',
                                                                height: '20px',
                                                                MozAppearance: 'textfield',
                                                            },
                                                        }}
                                                        InputProps={{
                                                            sx: {
                                                                '& input::-webkit-outer-spin-button': {
                                                                    WebkitAppearance: 'none',
                                                                    margin: 0,
                                                                },
                                                                '& input::-webkit-inner-spin-button': {
                                                                    WebkitAppearance: 'none',
                                                                    margin: 0,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                {/* Additional metrics when expanded */}
                                                <ClusterMetricsDisplay
                                                    clusterId={clusterId}
                                                    metrics={clusterMetrics[clusterId] ?? {}}
                                                    features={currentClusterFeatures[clusterId]}
                                                    allClustersData={currentClusterFeatures}
                                                    asset={clusterReturnSeries.asset}
                                                    showAllMetrics={showAllMetrics}
                                                    radarChartSize={200}
                                                />
                                            </Card>
                                        </MuiTooltip>
                                    </Grid>
                                ))}

                                {combinedMetrics && (hasEnabledCluster || hasEnabledRule) && (
                                    <Grid item xs={12} sm={6} md={2.4}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            gap={0.5}
                                            border={`3px solid ${theme.palette.primary.main}`}
                                            borderRadius={1}
                                            padding={1}
                                            bgcolor={theme.palette.primary.main + '05'} // 5% opacity
                                            height="100%" // Match the height of other boxes
                                            minHeight="fit-content" // Ensure minimum content height
                                        >
                                            {/* Title row - matches the cluster control row height */}
                                            <Box display="flex" alignItems="center" gap={0.3} minHeight="32px">
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                                    Combined Strategy
                                                </Typography>
                                            </Box>

                                            <ClusterMetricsDisplay
                                                clusterId={-1} // Special ID for combined strategy
                                                metrics={combinedMetrics}
                                                allClustersData={currentClusterFeatures}
                                                asset={clusterReturnSeries.asset}
                                                showAllMetrics={showAllMetrics}
                                                radarChartSize={200}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                            {/*Add the MetricRules component to your JSX */}
                            <MetricRules
                                timeseriesData={timeseriesData}
                                metricRules={metricRules}
                                onRulesChange={setMetricRules}
                            />
                        </Card>
                    </Box>
                )}

                {!loading && timeseriesData && timeseriesData.length > 0 && selectedClustering && (
                    <>
                        <Box mb={2} mt={4}>
                            <ClusteringPerformanceChart
                                clustering={selectedClustering}
                                clusteringAssignment={clusterAssignmentStrategy}
                                clusterIds={clusterIds}
                                cumulativeReturns={cumulativeReturns}
                                enabledClusters={enabledClusters}
                                combinedReturns={combinedReturns}
                                clusterReturnSeries={clusterReturnSeries}
                            />
                        </Box>
                        <Box mb={2}>
                            <ClusteringFeatureChart
                                clustering={selectedClustering}
                                timeseries={timeseriesData}
                                clusterReturnSeries={clusterReturnSeries}
                                clusterIds={clusterIds}
                                clusteringAssignment={clusterAssignmentStrategy}
                            />
                        </Box>
                        <Box mb={2}>
                            <ClusteringTimeChart
                                clustering={selectedClustering}
                                clusterIds={clusterIds}
                                clusterReturnSeries={clusterReturnSeries}
                                enabledClusters={enabledClusters}
                            />
                        </Box>
                        <Box mb={2}>
                            <ClusteringMetricsChart
                                clustering={selectedClustering}
                                clusterReturnSeries={clusterReturnSeries}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default ClusteringTab;